import React, { useEffect, useState } from "react";
import {
  Trophy,
  ChevronLeft,
  ChevronRight,
  Save,
  ArrowLeft,
} from "lucide-react";
import { useScoresStore } from "../../store/scoresStore";
import useAuthStore from "../../store/authStore";
import { PlayerRecord } from "../../components/PlayerRecord";
import { useConfirmationDialog } from "../../components/ConfirmDialog";
import { v4 as uuid } from "uuid";
import { formatToTimeStamp } from "../../utils/dateUtils";
import { PlayerDetails, Score } from "../../models/scoreModel";
import { TeamHeader } from "../../components/teams/TeamHeader";
import { MatchPlayScoreDataEntry } from "./MatchPlayScoreDataEntry";

export const ScoreDataEntry: React.FC<any> = () => {
  const { currentUser } = useAuthStore();

  const {
    allDetailsForScoreEntry,
    addScore,
    updateScore,
    getScoresToStartEntry,
  } = useScoresStore();

  const [currentHole, setCurrentHole] = useState(1);
  const [score, setScore] = useState<number | "">("");
  const [entryMode, setEntryMode] = useState<"flight" | "team" | "player">(
    "flight"
  );
  const [currentPlayerForScore, setCurrentPlayerForScore] =
    useState<PlayerDetails | null>(null);
  const [currentPlayer2ForScore, setCurrentPlayer2ForScore] =
    useState<PlayerDetails | null>(null);

  const { openDialog } = useConfirmationDialog();

  const isDoubles =
    allDetailsForScoreEntry?.matchSetupConfig?.playFormat === "doubles";

  // Get the scores for the current player
  useEffect(() => {
    if (currentUser) {
      getScoresToStartEntry(currentUser.playerId ?? "");
    }
  }, [currentUser, getScoresToStartEntry]);

  // Yardage data for each hole
  const getCurrentHoleData = (holeNumber: number) => {
    if (!allDetailsForScoreEntry) return null;
    const hole = allDetailsForScoreEntry.holes.find(
      (h: any) => h.holeNumber === holeNumber
    );
    return {
      holeNumber: hole?.holeNumber,
      par: hole?.par || 10,
      men: hole?.yardages.men.yardage || 4000,
      senior: hole?.yardages.seniors.yardage || 3800,
      ladies: hole?.yardages.ladies.yardage || 3600,
    };
  };

  const handlePrevHole = () => {
    handleSaveScore(false);

    if (currentHole > 1) {
      setCurrentHole((prev) => prev - 1);
    } else if (currentHole === 1) {
      setCurrentHole(18);
    }
  };

  const handleNextHole = (shouldSave = false) => {
    if (shouldSave) {
      handleSaveScore(false);
    }

    if (currentHole < 18) {
      setCurrentHole((prev) => prev + 1);
    } else if (currentHole === 18) {
      setCurrentHole(1);
    }
  };

  useEffect(() => {
    if (allDetailsForScoreEntry && allDetailsForScoreEntry.scores.length > 0) {
      const scores = (allDetailsForScoreEntry.scores ?? []) as Score[];
      const currentScore = scores.find(
        (s) =>
          s.holeNumber === currentHole &&
          ((s.scoreForPlayerId === currentPlayerForScore?.playerId &&
            s.scoreForPlayer2Id === currentPlayer2ForScore?.playerId) ||
            (s.scoreForPlayerId === currentPlayer2ForScore?.playerId &&
              s.scoreForPlayer2Id === currentPlayerForScore?.playerId))
      );

      if (currentScore) {
        setScore(currentScore.score);
      } else {
        setScore("");
      }
    }
  }, [
    allDetailsForScoreEntry,
    currentHole,
    currentPlayerForScore,
    currentPlayer2ForScore,
  ]);

  const handleScoreChange = (increment: boolean) => {
    if (score === "") {
      setScore(increment ? 1 : 12);
    } else {
      const newScore = increment
        ? Math.min((score as number) + 1, 12)
        : Math.max((score as number) - 1, 1);
      setScore(newScore);
    }
  };

  const handleSaveScore = (setNext = true) => {
    // check if the score already exists in the collection scoresByPlayer. If it does, confirm a change of score and update
    if (!allDetailsForScoreEntry || !currentPlayerForScore || !currentUser)
      return;
    const scores = (allDetailsForScoreEntry.scores ?? []) as Score[];
    // for player scores.
    let confirmAndUpdatePlayerScore = false;
    let existingScore = null;

    if (score !== "") {
      existingScore = scores.find((s) => {
        if (isDoubles) {
          return (
            s.holeNumber === currentHole &&
            ((s.scoreForPlayerId === currentPlayerForScore.playerId &&
              s.scoreForPlayer2Id === currentPlayer2ForScore?.playerId) ||
              (s.scoreForPlayerId === currentPlayer2ForScore?.playerId &&
                s.scoreForPlayer2Id === currentPlayerForScore?.playerId))
          );
        }
        return (
          s.holeNumber === currentHole &&
          s.scoreForPlayerId === currentPlayerForScore.playerId
        );
      });

      if (existingScore) {
        if (existingScore.score !== score) {
          confirmAndUpdatePlayerScore = true;
        }
      } else {
        // add new score for player.
        addScore({
          id: uuid(),
          matchId: allDetailsForScoreEntry?.matchSetupConfig?.matchId ?? "",
          seasonId: allDetailsForScoreEntry?.matchSetupConfig?.seasonId ?? "",
          holeNumber: currentHole ?? 0,
          holeDescription: currentHole?.toString() ?? "",
          scoreDateTime: formatToTimeStamp(new Date()),
          score: score as number,
          scoreType: "stroke",
          scoreStatus: "complete",
          scoreComment: "",
          scoreByType: "dataEntry",
          scoreByPlayerId: currentUser.id ?? "",
          scoreForPlayerId: currentPlayerForScore.playerId ?? "",
          scoreForPlayer2Id: isDoubles
            ? currentPlayer2ForScore?.playerId ?? ""
            : undefined,
          scoreByTeamId: "",
          scoreForTeamId: currentPlayerForScore.playerForTeamId ?? "",
          isUploaded: false,
          uploadDateTime: "",
        });
      }
    }

    if (confirmAndUpdatePlayerScore) {
      openDialog({
        title: "Update Score",
        message: `You have already entered a score for this hole. Do you want to update the scores?`,
        onConfirm: () => {
          if (confirmAndUpdatePlayerScore && existingScore) {
            updateScore({ ...existingScore, score: score as number });
          }
        },
      });
    }

    if (setNext) handleNextHole(false);
  };

  const renderScoreEntrySelector = () => (
    <div className="bg-white dark:bg-gray-800 rounded-sm shadow-md p-4">
      <div className="flex flex-col space-y-4">
        <div className="flex items-center space-x-4">
          <label className="text-gray-700 dark:text-gray-300 font-medium">
            Score Entry Mode:
          </label>
          <select
            title="Score Entry Mode"
            value={entryMode}
            onChange={(e) =>
              setEntryMode(e.target.value as "flight" | "team" | "player")
            }
            className="form-select rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 shadow-sm focus:border-blue-500 focus:ring-blue-500"
          >
            <option value="flight">By Flight</option>
            <option value="team">By Team</option>
            <option value="player">By Player</option>
          </select>
        </div>
        {entryMode === "flight" && (
          <div className="flex flex-col">
            {allDetailsForScoreEntry &&
              allDetailsForScoreEntry.matchSetupConfig &&
              allDetailsForScoreEntry.matchSetupConfig.flights &&
              Object.entries(
                allDetailsForScoreEntry?.matchSetupConfig.flights?.reduce(
                  (acc: any, flight: any) => {
                    const holeDescription = flight.holeDescription;
                    if (!acc[holeDescription]) {
                      acc[holeDescription] = [];
                    }
                    acc[holeDescription].push(flight);
                    return acc;
                  },
                  {}
                )
              ).map(([holeDescription, flights]: [string, any[] | unknown]) => (
                <div key={holeDescription}>
                  <div className="text-gray-700 dark:text-gray-300 font-medium mb-2">
                    {holeDescription}
                  </div>
                  {(flights as any[]).map((flight: any) => (
                    <div
                      className="flex items-center space-x-4 m-1"
                      onClick={() => {
                        const player = allDetailsForScoreEntry?.players?.find(
                          (p) => p.playerId === flight.playerId
                        );
                        const player2 = isDoubles
                          ? allDetailsForScoreEntry?.players?.find(
                              (p) => p.playerId === flight.player2Id
                            )
                          : null;
                        setCurrentPlayerForScore(player ?? null);
                        setCurrentPlayer2ForScore(player2 ?? null);
                      }}
                      key={flight.playerId}
                    >
                      <div className="flex flex-col">
                        <PlayerRecord
                          member={allDetailsForScoreEntry?.players?.find(
                            (p) => p.playerId === flight.playerId
                          )}
                          showHandicapAndMsl={false}
                        />
                        {isDoubles && flight.player2Id && (
                          <PlayerRecord
                            member={allDetailsForScoreEntry?.players?.find(
                              (p) => p.playerId === flight.player2Id
                            )}
                            showHandicapAndMsl={false}
                          />
                        )}
                      </div>
                      <ChevronRight className="h-6 w-6" />
                    </div>
                  ))}
                </div>
              ))}
          </div>
        )}

        {entryMode === "team" && (
          <div className="flex flex-col">
            {allDetailsForScoreEntry?.teams?.map((team: any) => (
              <div>
                <TeamHeader key={team.id} team={team} hideAvatar={true} />
                <div className="flex flex-col">
                  {isDoubles
                    ? allDetailsForScoreEntry?.matchSetupConfig?.playerSelections
                        ?.find((t) => t.teamId === team.id)
                        ?.players?.reduce(
                          (pairs: any[], player: any, index: number) => {
                            if (index % 2 === 0) {
                              const player2 =
                                allDetailsForScoreEntry?.matchSetupConfig?.playerSelections?.find(
                                  (t) => t.teamId === team.id
                                )?.players[index + 1];
                              if (player2) {
                                const pl1 =
                                  allDetailsForScoreEntry?.players?.find(
                                    (p) => p.playerId === player.playerId
                                  );
                                const pl2 =
                                  allDetailsForScoreEntry?.players?.find(
                                    (p) => p.playerId === player2.playerId
                                  );
                                pairs.push(
                                  <div
                                    className="flex items-center space-x-4 m-1"
                                    onClick={() => {
                                      setCurrentPlayerForScore(pl1 ?? null);
                                      setCurrentPlayer2ForScore(pl2 ?? null);
                                    }}
                                    key={`${player.playerId}-${player2.playerId}`}
                                  >
                                    <span className="text-gray-700 dark:text-gray-300">
                                      {Math.floor(index / 2) + 1}.
                                    </span>
                                    <div className="flex flex-col">
                                      <PlayerRecord
                                        member={pl1}
                                        showHandicapAndMsl={false}
                                      />
                                      <PlayerRecord
                                        member={pl2}
                                        showHandicapAndMsl={false}
                                      />
                                    </div>
                                    <ChevronRight className="h-6 w-6" />
                                  </div>
                                );
                              }
                            }
                            return pairs;
                          },
                          []
                        )
                    : allDetailsForScoreEntry?.matchSetupConfig?.playerSelections
                        ?.find((t) => t.teamId === team.id)
                        ?.players?.map((player, index) => {
                          const pl = allDetailsForScoreEntry?.players?.find(
                            (p: PlayerDetails) => p.playerId === player.playerId
                          );

                          return (
                            <div
                              className="flex items-center space-x-4 m-1"
                              onClick={() =>
                                setCurrentPlayerForScore(pl ?? null)
                              }
                              key={player.playerId}
                            >
                              <span className="text-gray-700 dark:text-gray-300">
                                {index + 1}.
                              </span>
                              <PlayerRecord
                                member={pl}
                                showHandicapAndMsl={false}
                              />
                              <ChevronRight className="h-6 w-6" />
                            </div>
                          );
                        })}
                </div>
              </div>
            ))}
          </div>
        )}

        {entryMode === "player" && (
          <div className="flex flex-col items-center space-x-4">
            <label className="text-gray-700 dark:text-gray-300 font-medium">
              Players
            </label>
            <div className="flex flex-col">
              {allDetailsForScoreEntry?.players
                ?.sort((a: any, b: any) => {
                  const firstNameComparison = a.firstName.localeCompare(
                    b.firstName
                  );
                  if (firstNameComparison !== 0) return firstNameComparison;
                  return a.lastName.localeCompare(b.lastName);
                })
                .map((player: any, index: number) => (
                  <div
                    className="flex items-center space-x-4 m-1"
                    onClick={() => setCurrentPlayerForScore(player)}
                    key={player.playerId}
                  >
                    <span className="text-gray-700 dark:text-gray-300">
                      {index + 1}.
                    </span>
                    <PlayerRecord member={player} showHandicapAndMsl={false} />
                    <ChevronRight className="h-6 w-6" />
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const renderPlayerInformation = () => (
    <div className="flex flex-col items-center mb-2">
      <PlayerRecord member={currentPlayerForScore} showHandicapAndMsl={false} />
      {isDoubles && currentPlayer2ForScore && (
        <PlayerRecord
          member={currentPlayer2ForScore}
          showHandicapAndMsl={false}
        />
      )}
    </div>
  );

  return (
    <div className="py-0 px-1 pb-6">
      {!allDetailsForScoreEntry && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-8">
          <div className="text-center text-gray-900 dark:text-white">
            <Trophy className="h-12 w-12 text-blue-500" />
            <h2 className="text-xl font-semibold mb-2">No Match in Progress</h2>
            <p className="text-gray-500 dark:text-gray-400">
              You are not currently in a match. Please check back later.
            </p>
          </div>
        </div>
      )}

      {allDetailsForScoreEntry &&
        allDetailsForScoreEntry.matchSetupConfig &&
        allDetailsForScoreEntry.matchSetupConfig.gameFormat === "matchplay" && (
          <>
            {!currentPlayerForScore && renderScoreEntrySelector()}
            {currentPlayerForScore && (
              <MatchPlayScoreDataEntry
                allDetailsForScoreEntry={allDetailsForScoreEntry}
                currentUser={currentPlayerForScore}
              />
            )}
          </>
        )}
      {allDetailsForScoreEntry &&
        allDetailsForScoreEntry.matchSetupConfig &&
        allDetailsForScoreEntry.matchSetupConfig.gameFormat !== "matchplay" && (
          <>
            {!currentPlayerForScore && renderScoreEntrySelector()}
            {currentPlayerForScore && (
              <>
                <div>
                  <ArrowLeft
                    className="h-6 w-6 text-gray-600 dark:text-gray-300"
                    onClick={() => {
                      setCurrentPlayerForScore(null);
                      setCurrentPlayer2ForScore(null);
                    }}
                  />
                </div>
                {/* Hole Navigation */}
                <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-4 mb-8">
                  <div className="flex flex-col items-center">
                    <div className="flex items-center justify-between w-full mb-4">
                      <button
                        onClick={handlePrevHole}
                        className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <ChevronLeft className="h-6 w-6" />
                      </button>
                      <div className="text-2xl font-bold text-gray-900 dark:text-white flex justify-center items-center space-x-4">
                        <span>Hole {currentHole}</span>
                        <div className="text-lg text-gray-500 dark:text-gray-400">
                          Par {getCurrentHoleData(currentHole)?.par}
                        </div>
                      </div>
                      <button
                        onClick={() => handleNextHole(true)}
                        className="p-2 rounded-lg text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50"
                      >
                        <ChevronRight className="h-6 w-6" />
                      </button>
                    </div>

                    <div className="text-center mb-6">
                      <div className="mt-1 flex justify-center space-x-4 text-sm">
                        <span className="text-blue-600 dark:text-blue-400 font-medium">
                          Men: {getCurrentHoleData(currentHole)?.men}y
                        </span>
                        <span className="text-yellow-600 dark:text-yellow-400 font-medium">
                          Senior: {getCurrentHoleData(currentHole)?.senior}y
                        </span>
                        <span className="text-pink-600 dark:text-pink-400 font-medium">
                          Ladies: {getCurrentHoleData(currentHole)?.ladies}y
                        </span>
                      </div>
                    </div>

                    {/* Score Input */}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-6 w-full max-w-2xl">
                      <div>
                        {renderPlayerInformation()}
                        <div className="flex items-center justify-center space-x-4">
                          <button
                            onClick={() => handleScoreChange(false)}
                            className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          >
                            -
                          </button>
                          <div className="w-20 text-center text-3xl font-bold text-gray-900 dark:text-white">
                            {score || "-"}
                          </div>
                          <button
                            onClick={() => handleScoreChange(true)}
                            className="w-12 h-12 flex items-center justify-center text-2xl font-bold bg-gray-100 dark:bg-gray-700 rounded-full hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300"
                          >
                            +
                          </button>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => handleSaveScore()}
                      disabled={score === ""}
                      className="w-full bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white py-3 rounded-lg font-medium flex items-center justify-center disabled:opacity-50 transition-colors"
                    >
                      <Save className="h-5 w-5 mr-2" />
                      Save Score
                    </button>
                  </div>
                </div>
              </>
            )}
          </>
        )}
    </div>
  );
};
